/**
 * StayLongerWidget — Iter 69.
 *
 * Conversion-boost card under the booking widget that shows the renter how
 * much extending their trip by 1, 2, or 3 days costs. Built on top of
 * `POST /api/cars/{car_id}/extension-preview`.
 *
 * Behaviour:
 *   - Hidden until the renter has picked both pickup and return dates.
 *   - Re-fetches when start/end changes (debounced 300 ms so dragging the
 *     date picker doesn't spam the API).
 *   - Each row shows: "+N day(s) = €total (+€delta)" plus a "Discount" pill
 *     when the new length unlocks a vendor's length-of-rental tier.
 *   - Clicking a row calls `onExtend(newEndIso)` which the parent uses to
 *     bump the booking widget's end date — the rest of the booking flow
 *     re-quotes automatically.
 *
 * Deliberately small (≤120 lines) — heavy lifting is on the backend.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { logWidgetEvent } from "@/lib/widgetTracking";
import { Calendar, TrendingUp, BadgePercent } from "lucide-react";
import Money from "@/components/Money";

const EXTRA_LABELS = { 1: "Stay 1 more day", 2: "Stay 2 more days", 3: "Stay 3 more days" };

export default function StayLongerWidget({ carId, startIso, endIso, onExtend }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!carId || !startIso || !endIso) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      api
        .post(`/cars/${carId}/extension-preview`, { start_date: startIso, end_date: endIso })
        .then((r) => {
          if (!cancelled) setPreview(r.data);
        })
        .catch((err) => {
          // Silent — this is a "nice to have" widget. A 400 (invalid dates)
          // shouldn't show a toast on every keystroke.
          console.debug("[StayLongerWidget] preview failed", err?.response?.status);
          if (!cancelled) setPreview(null);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [carId, startIso, endIso]);

  if (!preview || !preview.options?.length) return null;
  // Hide when total is zero (vendor mis-configured car at €0/day) — the
  // delta column would be meaningless.
  if (!preview.current?.total) return null;

  // Iter 69 — log impression once the card actually renders.
  logWidgetEvent("stay_longer", "impression", carId);

  return (
    <div
      className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-4 shadow-sm"
      data-testid="stay-longer-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center ring-1 ring-amber-200">
          <TrendingUp className="h-4 w-4" />
        </div>
        <div>
          <div className="font-display text-sm font-semibold text-neutral-900">Stay a little longer?</div>
          <div className="text-[11px] text-neutral-500">
            Currently {preview.current.days} day{preview.current.days === 1 ? "" : "s"} · <Money amount={preview.current.total} />
          </div>
        </div>
      </div>
      <ul className="space-y-1.5" data-testid="stay-longer-options">
        {preview.options.map((opt) => (
          <li key={opt.extra_days}>
            <button
              type="button"
              onClick={() => { logWidgetEvent("stay_longer", "click", carId, { extra_days: opt.extra_days }); onExtend?.(opt.extra_days); }}
              disabled={loading}
              className="w-full flex items-center justify-between gap-3 rounded-xl bg-white/80 hover:bg-white border border-amber-100 hover:border-amber-300 hover:shadow-md transition-all px-3 py-2.5 text-left disabled:opacity-50"
              data-testid={`stay-longer-option-${opt.extra_days}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Calendar className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                <span className="text-xs font-semibold text-neutral-800 truncate">{EXTRA_LABELS[opt.extra_days]}</span>
                {opt.discount_unlocked && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold ring-1 ring-emerald-200" data-testid={`stay-longer-discount-${opt.extra_days}`}>
                    <BadgePercent className="h-2.5 w-2.5" /> {opt.discount_percent}% off
                  </span>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-bold text-neutral-900 font-display">
                  <Money amount={opt.total} />
                </div>
                <div className="text-[10px] text-neutral-500">
                  +<Money amount={opt.delta} />
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
